import React, { ChangeEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  CardMedia,
  Input,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GridCloseIcon } from "@mui/x-data-grid";
import MainContainer from "../../layout/MainContainer";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ENUM_ROUTES } from "../../RoutesEnum";
import { UploadMedia } from "../../utils/mediaUpload";
import { showError, showToast } from "../../constants/toasts";
import { removeFalsyKeys } from "../../utils/nullKey";
import {
  useAddBlogMutation,
  useLazyGetBlogDetailQuery,
  useUpdateBlogMutation,
} from "../../services/blogs";

const initialValues = {
  title: "",
  description: "",
  authorName: "",
  webImage: "",
  // appImage: "",
};

const createValidationSchema = (isEdit: boolean) =>
  Yup.object({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    authorName: Yup.string().required("Author name is required"),
    webImage: Yup.string().required("Web image is required"),
    // appImage: Yup.string().required("App image is required"),
  });

const AddBlog = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [addBlog, { isLoading }] = useAddBlogMutation();
  const [updateBlog] = useUpdateBlogMutation();
  const [webImage, setWebImage] = useState<string>("");
  const [appImage, setAppImage] = useState<string>("");
  const [blogData, setBlogData] = useState<any>(null);
  const [blogDetails] = useLazyGetBlogDetailQuery();
  const [loading, setLoading] = useState(false);

  const getDataByIdList = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const response = await blogDetails({ id }).unwrap();
      if (response.statusCode === 200) {
        setBlogData(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };

  useEffect(() => {
    if (id) {
      getDataByIdList();
    }
  }, [id]);

  useEffect(() => {
    if (blogData) {
      formik.setValues({
        title: blogData.title || "",
        description: blogData.description || "",
        authorName: blogData.authorName || "",
        webImage: blogData.image || "",
      });
      setWebImage(blogData.image || "");
    }
  }, [blogData]);

  const formik = useFormik({
    initialValues,
    validationSchema: createValidationSchema(!!id),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!webImage) {
          showError("Web image is required");
          return;
        }

        const payload: any = {
          title: values.title,
          description: values.description,
          authorName: values.authorName,
          image: webImage,
          // appImage,
        };

        removeFalsyKeys(payload);

        let response;
        if (id) {
          response = await updateBlog({ body: payload, id }).unwrap();
        } else {
          response = await addBlog(payload).unwrap();
        }

        if (response?.statusCode === 200) {
          showToast(
            id ? "Blog updated successfully" : "Blog added successfully"
          );
          navigate(ENUM_ROUTES.MANAGE_BLOGS);
        }
      } catch (error: any) {
        console.error("Error details:", error);
        showError(
          error?.message || error?.data?.message || "Failed to save news"
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleWebImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      setWebImage(imageUrl.data);
      formik.setFieldValue("webImage", imageUrl.data);
    }
  };

  // const handleAppImageUpload = async (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const file = event.target?.files?.[0];
  //   if (!file) return;
  //   const imageUrl = (await UploadMedia(file)) as any;
  //   if (imageUrl?.data) {
  //     setAppImage(imageUrl.data);
  //     formik.setFieldValue("appImage", imageUrl.data);
  //   }
  // };

  return (
    <MainContainer>
      <div className="main_title">
        <h1>{id ? "Edit" : "Add"} Blog</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate(ENUM_ROUTES.MANAGE_BLOGS)}>
            Blog Management
          </span>
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            {/* Web Blog Upload */}
            <div className="control_group w_50">
              <label>Upload Image</label>
              {!webImage ? (
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    flexWrap: "nowrap",
                    marginTop: 8,
                    marginBottom: 20,
                    alignItems: "center",
                  }}
                >
                  <label
                    className="upload_image no_radius v2"
                    htmlFor="web-upload"
                    style={{
                      cursor: "pointer",
                      width: 300,
                      height: 150,
                      minWidth: 120,
                      minHeight: 120,
                      border: "1.5px dashed #ccc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#f5f5f5",
                      flex: "0 0 auto",
                    }}
                  >
                    <Input
                      id="web-upload"
                      type="file"
                      sx={{ display: "none" }}
                      inputProps={{ accept: "image/png,image/jpeg" }}
                      onChange={(event) => {
                        handleWebImageUpload(event as any);
                        event.target.value = "";
                      }}
                    />
                    <span
                      className="upload_image_holder"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src="/static/images/placeholder.png"
                        alt="icon"
                        style={{ width: 40, height: 40, opacity: 0.5 }}
                      />
                      <span className="upload_img_icon">
                        <img
                          src="/static/images/edit2_icon.svg"
                          alt="icon"
                          style={{ width: 24, height: 24 }}
                        />
                      </span>
                    </span>
                  </label>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    flexWrap: "nowrap",
                    marginTop: 8,
                    marginBottom: 20,
                    alignItems: "center",
                  }}
                >
                  <div
                    className="upload_image no_radius v2"
                    style={{
                      position: "relative",
                      width: 200,
                      height: 100,
                      minWidth: 120,
                      minHeight: 120,
                      border: "1.5px solid #ccc",
                      overflow: "visible",
                      background: "#fff",
                      flex: "0 0 auto",
                      marginRight: 20,
                    }}
                  >
                    <IconButton
                      size="small"
                      style={{
                        position: "absolute",
                        top: -10,
                        right: -10,
                        background: "#e71c57",
                        color: "#fff",
                        zIndex: 2,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() => {
                        setWebImage("");
                        formik.setFieldValue("webImage", "");
                      }}
                    >
                      <GridCloseIcon fontSize="small" />
                    </IconButton>
                    <div
                      className="upload_image_holder"
                      style={{ width: "100%", height: "100%" }}
                    >
                      <CardMedia
                        component="img"
                        image={webImage}
                        alt="Web News"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {formik.touched.webImage && formik.errors.webImage && (
                <Box className="error_text">{formik.errors.webImage}</Box>
              )}
            </div>

            {/* App News Upload */}
            <div className="control_group w_50"></div>

            {/* Title */}
            <div className="control_group w_50">
              <label>Title</label>
              <TextField
                fullWidth
                name="title"
                placeholder="Enter Title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />
            </div>

            {/* Author */}
            <div className="control_group w_50">
              <label>Author Name</label>
              <TextField
                fullWidth
                name="authorName"
                placeholder="Enter author name"
                value={formik.values.authorName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.authorName && Boolean(formik.errors.authorName)
                }
                helperText={
                  formik.touched.authorName && formik.errors.authorName
                }
              />
            </div>

            {/* Description */}
            <div className="control_group w_100">
              <label>Description</label>
              <TextField
                fullWidth
                multiline
                rows={4}
                name="description"
                placeholder="Enter description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
              />
            </div>
          </div>
          {/* Save Button */}
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              type="submit"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddBlog;
